import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Car, ShieldCheck, Loader2, FileCheck2, Landmark, BadgeCheck } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { api } from "@/lib/api";

const GUARANTEES = [
  { icon: Landmark, title: "Professionnels déclarés", desc: "Chaque loueur est une entreprise déclarée auprès de l'État marocain : Registre de Commerce (RC) et Identifiant Commun de l'Entreprise (ICE), l'équivalent du SIRET français." },
  { icon: FileCheck2, title: "Documents contrôlés", desc: "Statuts, patente et assurance des véhicules sont vérifiés par l'équipe link & rent avant toute mise en ligne." },
  { icon: BadgeCheck, title: "Contact direct et sécurisé", desc: "Vous échangez avec le propriétaire du véhicule via WhatsApp depuis chaque annonce, sans intermédiaire ni commission cachée." },
];

export default function Agencies() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/agencies/summary").then((r) => setSummary(r.data));
  }, []);

  return (
    <PublicLayout>
      <div className="lr-gradient-dark py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Nos agences partenaires</h1>
          <p className="mt-2 text-slate-300">Un réseau de professionnels déclarés et vérifiés, partout au Maroc.</p>
          {summary && (
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-2xl" data-testid="agencies-kpis">
              {[
                { label: "Loueurs professionnels", value: summary.total, icon: Building2, testid: "agencies-total" },
                { label: "Véhicules en ligne", value: summary.vehicles, icon: Car, testid: "agencies-vehicles" },
                { label: "Villes couvertes", value: summary.cities, icon: MapPin, testid: "agencies-cities" },
              ].map((k) => (
                <div key={k.label} className="rounded-2xl bg-white/10 backdrop-blur p-4 border border-white/10">
                  <k.icon className="h-5 w-5 text-amber-400 mb-2" />
                  <p className="font-display text-3xl font-extrabold text-white" data-testid={k.testid}>{k.value}</p>
                  <p className="text-xs text-slate-300">{k.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        <section>
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900 mb-6">Nos engagements</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {GUARANTEES.map((g) => (
              <div key={g.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`agencies-guarantee-${g.title}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl mb-4" style={{ background: "#0B132B" }}><g.icon className="h-5 w-5 text-white" /></span>
                <h3 className="font-bold text-slate-900">{g.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{g.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900 mb-6">Loueurs par ville</h2>
          {!summary ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="agencies-by-city">
              {summary.by_city.map((c) => (
                <Link key={c.city} to={`/cars?city=${encodeURIComponent(c.city)}`} data-testid={`agencies-city-${c.city}`} className="lr-card-hover rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: "#006233" }}><MapPin className="h-6 w-6 text-white" /></span>
                    <h3 className="font-display text-lg font-bold text-slate-900">{c.city}</h3>
                  </div>
                  <div className="mt-5 flex items-end justify-between">
                    <div>
                      <p className="font-display text-3xl font-extrabold text-slate-900">{c.agencies}</p>
                      <p className="text-xs uppercase tracking-wide text-slate-500">loueur{c.agencies > 1 ? "s" : ""} déclaré{c.agencies > 1 ? "s" : ""}</p>
                    </div>
                    <span className="flex items-center gap-1.5 text-sm text-slate-600"><Car className="h-4 w-4" /> {c.vehicles} véhicule{c.vehicles > 1 ? "s" : ""}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 text-xs border-t border-slate-100 pt-3" style={{ color: "#006233" }}>
                    <ShieldCheck className="h-3.5 w-3.5" /> RC & ICE vérifiés · Voir les véhicules
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-3xl p-8 flex flex-col md:flex-row md:items-center gap-6" style={{ background: "#0B132B" }}>
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style={{ background: "#25D366" }}><WhatsAppIcon className="h-8 w-8 text-white" /></span>
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold text-white">Comment contacter un loueur ?</h3>
            <p className="mt-1 text-sm text-slate-300">Pour garantir des échanges fiables, les coordonnées des loueurs ne sont pas publiées. Choisissez un véhicule, cliquez sur le logo WhatsApp et créez votre accès gratuit : vous êtes mis en relation directe avec le propriétaire.</p>
          </div>
          <Link to="/cars" data-testid="agencies-browse-cars-btn" className="inline-flex h-11 items-center justify-center rounded-md px-6 text-sm font-semibold text-white" style={{ background: "#C1272D" }}>Voir les véhicules</Link>
        </section>
      </div>
    </PublicLayout>
  );
}
